import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const API_AUTH_TOKEN = process.env.REACT_APP_API_AUTHORIZATION;

/** Initial fetch limit for faster first load. Omit or set to 0 to load all. */
const DEFAULT_INITIAL_PER_PAGE = 200;

/** Map tab name to API status filter. "processed" includes processed, prayed, completed. */
export const getStatusFilterForTab = (tab) => {
  if (tab === 'submitted') return 'new';
  if (tab === 'pending') return 'pending';
  if (tab === 'processed') return 'processed,prayed,completed';
  if (tab === 'deleted') return 'deleted';
  return null; // reports or other: no filter (fetch all)
};

export const PrayerRequestContext = createContext({
  prayerRequests: [],
  loading: false,
  error: null,
  counts: null,
  totalCount: null,
  fetchPrayerRequests: () => {},
  fetchCounts: () => {},
  createPrayerRequest: () => {},
  updatePrayerRequest: () => {},
  forwardPrayerRequest: () => {},
  recoverPrayerRequest: () => {},
});

export function PrayerRequestProvider({ children }) {
  const [prayerRequests, setPrayerRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [counts, setCounts] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const { user } = useAuth0();

  const prayerRequestsRef = useRef(prayerRequests);
  const lastFetchOptionsRef = useRef({});
  useEffect(() => {
    prayerRequestsRef.current = prayerRequests;
  }, [prayerRequests]);

  const fetchPrayerRequests = useCallback(
    async (options = {}) => {
      try {
        setLoading(true);
        setError(null);

        const {
          perPage = DEFAULT_INITIAL_PER_PAGE,
          page = 1,
          status,
          date_range: dateRange,
        } = options;

        lastFetchOptionsRef.current = options;

        const params = new URLSearchParams();
        if (perPage > 0) {
          params.set('per_page', String(perPage));
          params.set('page', String(page));
        }
        if (status != null && status !== '') {
          params.set('status', String(status));
        }
        if (dateRange != null && dateRange !== '') {
          params.set('date_range', String(dateRange));
        }
        const query = params.toString();
        const url = query
          ? `${API_BASE_URL}/prayer-basket?${query}`
          : `${API_BASE_URL}/prayer-basket`;

        const response = await fetch(url, {
          headers: {
            Authorization: `${API_AUTH_TOKEN}`,
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) throw new Error('Failed to fetch prayer requests');

        const data = await response.json();
        const list = Array.isArray(data.data) ? data.data : [];
        setPrayerRequests(list);

        if (data.meta) {
          if (typeof data.meta.total === 'number') {
            setTotalCount(data.meta.total);
          }
          if (data.meta.counts && typeof data.meta.counts === 'object') {
            const c = data.meta.counts;
            setCounts({
              submitted: c.submitted ?? c.new ?? 0,
              pending: c.pending ?? 0,
              processed: c.processed ?? 0,
              deleted: c.deleted ?? 0,
            });
          }
        } else {
          setTotalCount(list.length);
        }
      } catch (err) {
        setError('Failed to fetch prayer requests');
        toast.error('Failed to fetch prayer requests');
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const refetchWithLastOptions = useCallback(() => {
    fetchPrayerRequests(lastFetchOptionsRef.current);
  }, [fetchPrayerRequests]);

  const computeCounts = useCallback((list) => {
    if (!Array.isArray(list)) return null;
    return {
      submitted: list.filter((r) => r.status === 'new').length,
      pending: list.filter((r) => r.status === 'pending').length,
      processed: list.filter(
        (r) =>
          r.status === 'processed' ||
          r.status === 'prayed' ||
          r.status === 'completed'
      ).length,
      deleted: list.filter((r) => r.status === 'deleted').length,
    };
  }, []);

  const fetchCounts = useCallback(async () => {
    try {
      const url = `${API_BASE_URL}/prayer-basket?per_page=1&page=1`;
      const response = await fetch(url, {
        headers: {
          Authorization: `${API_AUTH_TOKEN}`,
          'Content-Type': 'application/json',
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const list = Array.isArray(data.data) ? data.data : [];
      const metaCounts = data.meta?.counts;
      if (metaCounts && typeof metaCounts === 'object') {
        setCounts(metaCounts);
      } else {
        setCounts(computeCounts(list));
      }
    } catch (err) {
      console.error('Failed to fetch counts', err);
    }
  }, [computeCounts]);

  const createPrayerRequest = useCallback(
    async (newRequest) => {
      try {
        setError(null);

        const response = await fetch(`${API_BASE_URL}/prayer-basket`, {
          method: 'POST',
          headers: {
            Authorization: `${API_AUTH_TOKEN}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...newRequest,
            userId: user?.sub,
            organizationId: user?.organizationId,
            status: newRequest.status ?? 'pending',
          }),
        });

        if (!response.ok) throw new Error('Failed to create prayer request');

        const data = await response.json();
        const createdRequest = data.data != null ? data.data : data;
        setPrayerRequests((prev) =>
          Array.isArray(prev) ? [createdRequest, ...prev] : [createdRequest]
        );

        refetchWithLastOptions();
        fetchCounts();
        toast.success('Prayer request created successfully');
        return createdRequest;
      } catch (err) {
        setError('Failed to create prayer request');
        toast.error('Failed to create prayer request');
        console.error(err);
        throw err;
      }
    },
    [user?.sub, user?.organizationId, refetchWithLastOptions, fetchCounts]
  );

  const updatePrayerRequest = useCallback(
    async (id, updates) => {
      const current = prayerRequestsRef.current;
      const originalRequests = [...current];
      const optimisticUpdate = current.map((req) =>
        req.id === id ? { ...req, ...updates } : req
      );
      setPrayerRequests(optimisticUpdate);

      try {
        setError(null);

        const response = await fetch(`${API_BASE_URL}/prayer-basket/${id}`, {
          method: 'PUT',
          headers: {
            Authorization: `${API_AUTH_TOKEN}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...updates,
            organizationId: user?.organizationId,
          }),
        });

        const res = await response.json();

        if (!response.ok) {
          setPrayerRequests(originalRequests);
          throw new Error(
            res.message || `Failed to update prayer request: ${response.status}`
          );
        }

        if (res.success === false) {
          setPrayerRequests(originalRequests);
          throw new Error(res.message || 'Prayer request not found');
        }

        const updatedRequest = res.data != null ? res.data : res;
        setPrayerRequests((prev) =>
          prev.map((req) => (req.id === id ? updatedRequest : req))
        );

        refetchWithLastOptions();
        fetchCounts();
        toast.success('Prayer request updated successfully');
        return updatedRequest;
      } catch (err) {
        setError('Failed to update prayer request');
        toast.error(err.message || 'Failed to update prayer request');
        setPrayerRequests(originalRequests);
        throw err;
      }
    },
    [user?.organizationId, refetchWithLastOptions, fetchCounts]
  );

  const forwardPrayerRequest = useCallback(
    async (id, recipientData) => {
      const current = prayerRequestsRef.current;
      const originalRequests = [...current];
      const optimisticUpdate = current.map((req) =>
        req.id === id
          ? {
              ...req,
              status: 'processed',
              forwardedTo: recipientData.recipientEmail,
              forwardedAt: new Date().toISOString(),
            }
          : req
      );
      setPrayerRequests(optimisticUpdate);

      try {
        setError(null);

        const response = await fetch(
          `${API_BASE_URL}/prayer-basket/${id}/forward-email`,
          {
            method: 'POST',
            headers: {
              Authorization: `${API_AUTH_TOKEN}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              recipientEmail: recipientData.recipientEmail,
              recipientName: recipientData.recipientName,
              organizationId: user?.organizationId,
            }),
          }
        );

        const res = await response.json();

        if (!response.ok) {
          setPrayerRequests(originalRequests);
          throw new Error(
            res.message || 'Failed to forward prayer request'
          );
        }

        const updatedRequest = res.data != null ? res.data : res;
        setPrayerRequests((prev) =>
          prev.map((req) => (req.id === id ? updatedRequest : req))
        );

        refetchWithLastOptions();
        fetchCounts();
        toast.success('Prayer request forwarded successfully');
        return res;
      } catch (err) {
        setError('Failed to forward prayer request');
        toast.error(err.message || 'Failed to forward prayer request');
        setPrayerRequests(originalRequests);
        console.error(err);
        throw err;
      }
    },
    [user?.organizationId, refetchWithLastOptions, fetchCounts]
  );

  const recoverPrayerRequest = useCallback(async (id) => {
    const current = prayerRequestsRef.current;
    const originalRequests = [...current];
    const optimisticUpdate = current.map((req) =>
      req.id === id ? { ...req, status: 'new' } : req
    );
    setPrayerRequests(optimisticUpdate);

    try {
      setError(null);

      const response = await fetch(
        `${API_BASE_URL}/prayer-basket/${id}/recover`,
        {
          method: 'POST',
          headers: {
            Authorization: `${API_AUTH_TOKEN}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const res = await response.json();

      if (!response.ok) {
        setPrayerRequests(originalRequests);
        throw new Error('Failed to recover prayer request');
      }

      if (res.success === false) {
        setPrayerRequests(originalRequests);
        throw new Error(res.message || 'Failed to recover prayer request');
      }

      const recoveredRequest = res.data != null ? res.data : res;
      setPrayerRequests((prev) =>
        prev.map((req) => (req.id === id ? recoveredRequest : req))
      );

      refetchWithLastOptions();
      fetchCounts();
      toast.success('Prayer request recovered successfully');
      return res;
    } catch (err) {
      setError('Failed to recover prayer request');
      toast.error('Failed to recover prayer request');
      setPrayerRequests(originalRequests);
      console.error(err);
      throw err;
    }
  }, [refetchWithLastOptions, fetchCounts]);

  const value = useMemo(
    () => ({
      prayerRequests,
      loading,
      error,
      counts,
      totalCount,
      fetchPrayerRequests,
      fetchCounts,
      createPrayerRequest,
      updatePrayerRequest,
      forwardPrayerRequest,
      recoverPrayerRequest,
    }),
    [
      prayerRequests,
      loading,
      error,
      counts,
      totalCount,
      fetchPrayerRequests,
      fetchCounts,
      createPrayerRequest,
      updatePrayerRequest,
      forwardPrayerRequest,
      recoverPrayerRequest,
    ]
  );

  return (
    <PrayerRequestContext.Provider value={value}>
      {children}
    </PrayerRequestContext.Provider>
  );
}

export const usePrayerRequests = () => useContext(PrayerRequestContext);
